/* SAJAT - WELLPOINT Service Bundle
 * VERSION: 3.0.0
 * UPDATED: 2026-09-10
 *
 * ==========================================================
 * LOGIC
 * ==========================================================
 *
 * A Device NEM SKU / Variant ID alapján van definiálva.
 *
 * Device:
 *   - product title megfelel az opcionális title filternek
 *   - ÉS valamely variant option értéke:
 *       PRO
 *       PLUS
 *       LITE
 *
 * Service:
 *   PLAN -> Service product handle
 *   a settings.js fájlból.
 *
 *
 * DEVICE -> SERVICE
 *
 * PRO Device qty összesen  = PRO Service qty
 * PLUS Device qty összesen = PLUS Service qty
 * LITE Device qty összesen = LITE Service qty
 *
 *
 * SERVICE +/-:
 *
 * Ha pontosan:
 *   1 Device line
 *   1 Service line
 *
 * akkor:
 *   Service +/- -> Device +/-
 *
 * Több Device line esetén:
 *   Service kézi módosítása nem egyértelmű.
 *   Figyelmeztetés + Service visszaáll.
 *
 *
 * SERVICE REMOVE:
 *
 * Default:
 *   REMOVE_DEVICES
 *
 * Service törlés ->
 * az adott Plan összes Device line-ja törlődik.
 *
 * ==========================================================
 */

(() => {
  'use strict';


  /*
   * ========================================================
   * VERSION
   * ========================================================
   */

  const SCRIPT_VERSION =
    '3.0.0';


  /*
   * ========================================================
   * DEBUG
   * ========================================================
   *
   * true  = Console log ON
   * false = Console log OFF
   */

  //const CAN_WRITE_CONSOLE_LOG = true;
  const CAN_WRITE_CONSOLE_LOG = false;


  /*
   * ========================================================
   * SERVICE REMOVE BEHAVIOR
   * ========================================================
   *
   * DEFAULT:
   *
   * REMOVE_DEVICES
   *
   * Optional alternative:
   *
   * RESTORE_SERVICE
   */

  const SERVICE_REMOVE_BEHAVIOR =
    'REMOVE_DEVICES';


  /*
   * ========================================================
   * TIMING
   * ========================================================
   */

  const PAGE_REFRESH_DELAY_MS =
    200;

  const BACKEND_WAIT_MS =
    3000;

  const BACKEND_POLL_MS =
    100;

  const ADD_MONITOR_MS =
    3000;

  const WARNING_DISPLAY_MS =
    4500;


  /*
   * ========================================================
   * GLOBAL VERSION
   * ========================================================
   */

  window.WELLPOINT_BUNDLE_SCRIPT_VERSION =
    SCRIPT_VERSION;

  window.WELLPOINT_BUNDLE_SCRIPT = {
    version: SCRIPT_VERSION,
    active: true
  };


  /*
   * ========================================================
   * LOG
   * ========================================================
   */

  function log(...args) {
    if (CAN_WRITE_CONSOLE_LOG) {
      console.info(...args);
    }
  }


  function warn(...args) {
    if (CAN_WRITE_CONSOLE_LOG) {
      console.warn(...args);
    }
  }


  function error(...args) {
    console.error(...args);
  }


  log(
    `[WELLPOINT Bundle] v${SCRIPT_VERSION} loaded`
  );


  /*
   * ========================================================
   * SETTINGS
   * ========================================================
   */

  const SETTINGS =
    window.WELLPOINT_SERVICE_BUNDLE_SETTINGS;


  if (
    !SETTINGS ||
    !SETTINGS.plans
  ) {

    error(
      '[WELLPOINT] Bundle settings not loaded.'
    );

    return;
  }


  log(
    `[WELLPOINT] Settings v${SETTINGS.version || 'unknown'} loaded`
  );


  /*
   * ========================================================
   * CONSTANTS
   * ========================================================
   */

  const VALID_PLANS = [
    'PRO',
    'PLUS',
    'LITE'
  ];


  const ROOT =
    window.Shopify?.routes?.root ||
    '/';


  const originalFetch =
    window.fetch.bind(window);


  const QUANTITY_SELECTOR = [
    'quantity-input input',
    'input.quantity__input',
    'input[name="updates[]"]'
  ].join(',');


  const REMOVE_SELECTOR = [
    'cart-remove-button',
    'a[id^="Remove-"]',
    'a[id^="CartRemove-"]',
    'a[id^="CartDrawer-Remove-"]',
    'button[id^="Remove-"]',
    'button[id^="CartRemove-"]',
    'button[id^="CartDrawer-Remove-"]'
  ].join(',');


  /*
   * ========================================================
   * STATE
   * ========================================================
   */

  let cartSnapshot =
    null;


  let refreshTimer =
    null;


  let pageReloading =
    false;


  let refreshNeeded =
    false;


  let warningTimer =
    null;


  const serviceRuntime =
    new Map();


  const pendingChanges =
    new Map();


  const runningWorkers =
    new Map();


  /*
   * ========================================================
   * UTIL
   * ========================================================
   */

  function sleep(ms) {

    return new Promise(
      resolve =>
        setTimeout(
          resolve,
          ms
        )
    );
  }


  function normalize(value) {

    return String(
      value || ''
    )
      .trim()
      .toUpperCase();
  }


  /*
   * ========================================================
   * PLAN CONFIG
   * ========================================================
   */

  function getPlanHandle(plan) {

    return (
      SETTINGS.plans?.[plan] ||
      null
    );
  }


  /*
   * ========================================================
   * CART
   * ========================================================
   */

  async function getCart() {

    const response =
      await originalFetch(
        ROOT + 'cart.js',
        {
          headers: {
            Accept:
              'application/json'
          },
          cache:
            'no-store'
        }
      );


    if (!response.ok) {

      throw new Error(
        'Could not read Shopify cart.'
      );
    }


    return response.json();
  }


  async function refreshCartSnapshot() {

    cartSnapshot =
      await getCart();


    return cartSnapshot;
  }


  /*
   * ========================================================
   * SELLING PLAN ID
   * ========================================================
   */

  function getSellingPlanId(item) {

    return (

      item
        ?.selling_plan_allocation
        ?.selling_plan
        ?.id ||

      item
        ?.selling_plan_allocation
        ?.selling_plan_id ||

      null

    );
  }


  /*
   * ========================================================
   * SERVICE RESOLVE
   * ========================================================
   *
   * PLAN -> HANDLE
   *
   * A product JSON-ból automatikusan:
   *
   * variant ID
   * selling plan ID
   * SKU
   */

  async function resolveService(plan) {

    if (
      serviceRuntime.has(plan)
    ) {

      return serviceRuntime.get(plan);
    }


    const handle =
      getPlanHandle(plan);


    if (!handle) {

      throw new Error(
        `${plan}: Service handle missing.`
      );
    }


    const response =
      await originalFetch(
        ROOT +
        'products/' +
        handle +
        '.js',
        {
          headers: {
            Accept:
              'application/json'
          },
          cache:
            'no-store'
        }
      );


    if (!response.ok) {

      throw new Error(
        `${plan}: Service product could not be loaded.`
      );
    }


    const product =
      await response.json();


    /*
     * Current architecture:
     * one Service variant per product.
     */

    const variant =
      product
        ?.variants
        ?.[0];


    if (!variant) {

      throw new Error(
        `${plan}: Service variant not found.`
      );
    }


    const sellingPlanId =

      variant
        ?.selling_plan_allocations
        ?.[0]
        ?.selling_plan_id ||

      product
        ?.selling_plan_groups
        ?.[0]
        ?.selling_plans
        ?.[0]
        ?.id ||

      null;


    if (!sellingPlanId) {

      throw new Error(
        `${plan}: Selling Plan ID not found.`
      );
    }


    const result = {

      plan:
        plan,

      handle:
        handle,

      productId:
        String(
          product.id
        ),

      variantId:
        String(
          variant.id
        ),

      sku:
        variant.sku || '',

      sellingPlanId:
        String(
          sellingPlanId
        )

    };


    serviceRuntime.set(
      plan,
      result
    );


    log(
      `[WELLPOINT] ${plan} Service resolved:`,
      result
    );


    return result;
  }


  async function resolveAllServices() {

    for (
      const plan of
      VALID_PLANS
    ) {

      try {

        await resolveService(
          plan
        );

      } catch (e) {

        error(
          `[WELLPOINT] ${plan} Service resolution failed:`,
          e
        );
      }
    }
  }


  /*
   * ========================================================
   * IS SERVICE LINE
   * ========================================================
   */

  function getServicePlan(item) {

    if (!item) {
      return null;
    }


    for (
      const plan of
      VALID_PLANS
    ) {

      const service =
        serviceRuntime.get(plan);


      if (!service) {
        continue;
      }


      /*
       * Variant ID is strongest.
       */

      if (
        String(item.variant_id) ===
        String(service.variantId)
      ) {

        return plan;
      }


      /*
       * SKU fallback.
       */

      if (
        service.sku &&
        item.sku ===
        service.sku
      ) {

        return plan;
      }
    }


    return null;
  }


  /*
   * ========================================================
   * PRODUCT TITLE FILTER
   * ========================================================
   */

  function passesDeviceTitleFilter(
    item
  ) {

    const filter =
      normalize(
        SETTINGS.deviceProductTitleFilter
      );


    /*
     * Empty =
     * everybody passes.
     */

    if (!filter) {

      return true;
    }


    const title =
      normalize(
        item.product_title ||
        item.title
      );


    /*
     * Contains comparison.
     *
     * BUNDLE matches:
     *
     * "BUNDLE: People Counter..."
     * "My Bundle Product"
     * etc.
     */

    return title.includes(
      filter
    );
  }


  /*
   * ========================================================
   * GET VARIANT OPTIONS
   * ========================================================
   */

  function getItemOptionValues(item) {

    const values =
      [];


    /*
     * Shopify cart.js:
     * variant_options
     */

    if (
      Array.isArray(
        item?.variant_options
      )
    ) {

      item.variant_options.forEach(
        value => {

          values.push(
            normalize(value)
          );
        }
      );
    }


    /*
     * Fallback:
     * options_with_values
     */

    if (
      Array.isArray(
        item?.options_with_values
      )
    ) {

      item.options_with_values.forEach(
        option => {

          values.push(
            normalize(
              option?.value
            )
          );
        }
      );
    }


    return values;
  }


  /*
   * ========================================================
   * DEVICE PLAN
   * ========================================================
   *
   * This is the central simplified logic.
   *
   * NO SKU
   * NO DEVICE VARIANT ID
   */

  function getDevicePlan(item) {

    if (!item) {

      return null;
    }


    /*
     * Never classify Service itself
     * as Device.
     */

    if (
      getServicePlan(item)
    ) {

      return null;
    }


    /*
     * Optional title filter.
     */

    if (
      !passesDeviceTitleFilter(item)
    ) {

      return null;
    }


    const values =
      getItemOptionValues(item);


    for (
      const plan of
      VALID_PLANS
    ) {

      if (
        values.includes(plan)
      ) {

        return plan;
      }
    }


    return null;
  }


  /*
   * ========================================================
   * DEVICE LINES FOR PLAN
   * ========================================================
   */

  function getDeviceLines(
    cart,
    plan
  ) {

    return (
      cart?.items?.filter(
        item =>
          getDevicePlan(item) ===
          plan
      ) ||
      []
    );
  }


  /*
   * ========================================================
   * DEVICE TOTAL
   * ========================================================
   */

  function getDeviceTotal(
    cart,
    plan
  ) {

    return getDeviceLines(
      cart,
      plan
    ).reduce(
      (total, item) =>

        total +
        (
          Number(
            item.quantity
          ) || 0
        ),

      0
    );
  }


  /*
   * ========================================================
   * SERVICE LINES
   * ========================================================
   */

  function getServiceLines(
    cart,
    plan
  ) {

    return (
      cart?.items?.filter(
        item =>
          getServicePlan(item) ===
          plan
      ) ||
      []
    );
  }


  /*
   * ========================================================
   * IDENTIFY ITEM
   * ========================================================
   */

  function identifyItem(item) {

    const servicePlan =
      getServicePlan(item);


    if (servicePlan) {

      return {
        type: 'service',
        plan: servicePlan
      };
    }


    const devicePlan =
      getDevicePlan(item);


    if (devicePlan) {

      return {
        type: 'device',
        plan: devicePlan
      };
    }


    return null;
  }


  /*
   * ========================================================
   * ADD SERVICE
   * ========================================================
   */

  async function addService(
    plan,
    quantity
  ) {

    if (
      quantity <= 0
    ) {

      return false;
    }


    const service =
      await resolveService(plan);


    log(
      `[WELLPOINT] ${plan}: SERVICE MISSING -> ADD qty=${quantity}`
    );


    const response =
      await originalFetch(
        ROOT + 'cart/add.js',
        {

          method:
            'POST',

          headers: {

            'Content-Type':
              'application/json',

            Accept:
              'application/json'

          },

          body:
            JSON.stringify({

              items: [
                {

                  id:
                    service.variantId,

                  quantity:
                    quantity,

                  selling_plan:
                    service.sellingPlanId,

                  properties: {

                    _wellpoint_bundle_plan:
                      plan

                  }
                }
              ]
            })
        }
      );


    if (!response.ok) {

      const message =
        await response.text();


      throw new Error(
        `${plan}: Service add failed: ${message}`
      );
    }


    log(
      `[WELLPOINT] ${plan}: Service added qty=${quantity}`
    );


    return true;
  }


  /*
   * ========================================================
   * UPDATE CART
   * ========================================================
   */

  async function updateCart(
    updates
  ) {

    if (
      !updates ||
      Object.keys(updates).length === 0
    ) {

      return false;
    }


    log(
      '[WELLPOINT] Updating cart:',
      updates
    );


    const response =
      await originalFetch(
        ROOT + 'cart/update.js',
        {

          method:
            'POST',

          headers: {

            'Content-Type':
              'application/json',

            Accept:
              'application/json'

          },

          body:
            JSON.stringify({
              updates:
                updates
            })
        }
      );


    if (!response.ok) {

      const message =
        await response.text();


      throw new Error(
        'Cart update failed: ' +
        message
      );
    }


    return true;
  }


  /*
   * ========================================================
   * SYNC PLAN
   * ========================================================
   *
   * Service qty =
   * SUM Device qty.
   */

  async function syncPlan(
    plan,
    suppliedCart = null
  ) {

    const cart =
      suppliedCart ||
      await getCart();


    const devices =
      getDeviceLines(
        cart,
        plan
      );


    const targetQty =
      getDeviceTotal(
        cart,
        plan
      );


    const services =
      getServiceLines(
        cart,
        plan
      );


    log(
      `[WELLPOINT] ${plan}: Devices=${devices.length}, Device qty=${targetQty}, Service lines=${services.length}`
    );


    /*
     * No Device ->
     * remove orphan Service.
     */

    if (
      targetQty === 0
    ) {

      if (
        services.length === 0
      ) {

        return false;
      }


      const updates = {};


      services.forEach(
        service => {

          updates[
            service.key
          ] = 0;
        }
      );


      await updateCart(
        updates
      );


      return true;
    }


    /*
     * Device exists,
     * Service missing.
     */

    if (
      services.length === 0
    ) {

      await addService(
        plan,
        targetQty
      );


      return true;
    }


    /*
     * Service exists.
     */

    const primary =
      services[0];


    const updates = {};


    if (
      Number(primary.quantity) !==
      Number(targetQty)
    ) {

      log(
        `[WELLPOINT] ${plan}: Service ${primary.quantity} -> ${targetQty}`
      );


      updates[
        primary.key
      ] = targetQty;
    }


    /*
     * Remove duplicate Service lines.
     */

    services
      .slice(1)
      .forEach(
        service => {

          updates[
            service.key
          ] = 0;
        }
      );


    if (
      Object.keys(updates).length === 0
    ) {

      return false;
    }


    await updateCart(
      updates
    );


    return true;
  }


  /*
   * ========================================================
   * SYNC ALL
   * ========================================================
   */

  async function syncAllPlans() {

    let changed =
      false;


    let cart =
      await getCart();


    for (
      const plan of
      VALID_PLANS
    ) {

      const planChanged =
        await syncPlan(
          plan,
          cart
        );


      if (planChanged) {

        changed =
          true;


        cart =
          await getCart();
      }
    }


    cartSnapshot =
      cart;


    return changed;
  }


  /*
   * ========================================================
   * WARNING
   * ========================================================
   */

  function showServiceWarning(
    plan
  ) {

    document
      .getElementById(
        'wellpoint-service-qty-warning'
      )
      ?.remove();


    if (warningTimer) {

      clearTimeout(
        warningTimer
      );
    }


    const div =
      document.createElement(
        'div'
      );


    div.id =
      'wellpoint-service-qty-warning';


    div.innerHTML =
      `<strong>${plan} service quantity is based on the total number of devices.</strong>` +
      `<br>Please adjust the device quantities instead.`;


    div.style.cssText = `
      position:fixed;
      left:50%;
      bottom:24px;
      transform:translateX(-50%);
      z-index:999999;
      width:calc(100% - 32px);
      max-width:520px;
      box-sizing:border-box;
      padding:11px 16px;
      border-radius:7px;
      background:rgba(25,25,25,.94);
      color:#fff;
      font-family:inherit;
      font-size:13px;
      line-height:1.45;
      text-align:center;
      box-shadow:0 3px 14px rgba(0,0,0,.18);
      pointer-events:none;
    `;


    document.body.appendChild(
      div
    );


    warningTimer =
      setTimeout(
        () => {

          div.remove();

          warningTimer =
            null;
        },

        WARNING_DISPLAY_MS
      );
  }


  /*
   * ========================================================
   * REFRESH
   * ========================================================
   */

  function cancelRefresh() {

    if (refreshTimer) {

      clearTimeout(
        refreshTimer
      );


      refreshTimer =
        null;


      log(
        '[WELLPOINT] Pending refresh cancelled'
      );
    }
  }


  function markRefreshNeeded() {

    refreshNeeded =
      true;


    cancelRefresh();
  }


  function scheduleRefresh() {

    if (
      !refreshNeeded ||
      pageReloading ||
      pendingChanges.size > 0 ||
      runningWorkers.size > 0
    ) {

      return;
    }


    cancelRefresh();


    log(
      `[WELLPOINT] Refresh in ${PAGE_REFRESH_DELAY_MS} ms`
    );


    refreshTimer =
      setTimeout(
        () => {

          refreshTimer =
            null;


          if (
            pendingChanges.size > 0 ||
            runningWorkers.size > 0
          ) {

            scheduleRefresh();

            return;
          }


          refreshNeeded =
            false;


          pageReloading =
            true;


          window.location.reload();

        },

        PAGE_REFRESH_DELAY_MS
      );
  }


  /*
   * ========================================================
   * DOM LINE INDEX
   * ========================================================
   */

  function getLineIndex(element) {

    if (!element) {
      return 0;
    }


    const direct =
      parseInt(
        element.dataset?.index,
        10
      );


    if (direct > 0) {
      return direct;
    }


    const holder =
      element.closest?.(
        '[data-index]'
      );


    const holderIndex =
      parseInt(
        holder?.dataset?.index,
        10
      );


    if (holderIndex > 0) {
      return holderIndex;
    }


    const ids = [
      element.id,
      holder?.id
    ];


    for (
      const id of
      ids
    ) {

      const match =
        String(
          id || ''
        ).match(
          /(\d+)$/
        );


      if (match) {

        return (
          parseInt(
            match[1],
            10
          ) || 0
        );
      }
    }


    return 0;
  }


  /*
   * ========================================================
   * WAIT SHOPIFY BACKEND
   * ========================================================
   */

  async function waitForBackend(
    sourceItem,
    expectedQty
  ) {

    const started =
      Date.now();


    let latest =
      null;


    while (
      Date.now() -
      started <
      BACKEND_WAIT_MS
    ) {

      await sleep(
        BACKEND_POLL_MS
      );


      latest =
        await getCart();


      const current =
        latest.items.find(
          item =>
            item.key ===
            sourceItem.key
        );


      if (
        expectedQty === 0 &&
        !current
      ) {

        return latest;
      }


      if (
        current &&
        Number(current.quantity) ===
        Number(expectedQty)
      ) {

        return latest;
      }
    }


    warn(
      '[WELLPOINT] Backend wait timeout:',
      sourceItem.title,
      expectedQty
    );


    return (
      latest ||
      await getCart()
    );
  }


  /*
   * ========================================================
   * REMOVE ALL DEVICES FOR PLAN
   * ========================================================
   */

  async function removeDevicesForPlan(
    plan,
    suppliedCart = null
  ) {

    const cart =
      suppliedCart ||
      await getCart();


    const updates = {};


    getDeviceLines(
      cart,
      plan
    ).forEach(
      item => {

        updates[
          item.key
        ] = 0;
      }
    );


    /*
     * Defensive:
     * remove Service too.
     */

    getServiceLines(
      cart,
      plan
    ).forEach(
      item => {

        updates[
          item.key
        ] = 0;
      }
    );


    log(
      `[WELLPOINT] ${plan}: REMOVE_DEVICES`
    );


    await updateCart(
      updates
    );
  }


  /*
   * ========================================================
   * SERVICE REMOVAL
   * ========================================================
   */

  async function handleServiceRemoval(
    plan,
    cart
  ) {

    if (
      SERVICE_REMOVE_BEHAVIOR ===
      'REMOVE_DEVICES'
    ) {

      await removeDevicesForPlan(
        plan,
        cart
      );


      return;
    }


    /*
     * Optional alternate behavior.
     */

    await syncPlan(
      plan,
      cart
    );
  }


  /*
   * ========================================================
   * SERVICE QTY -> SINGLE DEVICE
   * ========================================================
   */

  async function serviceToSingleDevice(
    plan,
    sourceItem,
    desiredQty
  ) {

    const cart =
      await waitForBackend(
        sourceItem,
        desiredQty
      );


    const devices =
      getDeviceLines(
        cart,
        plan
      );


    const services =
      getServiceLines(
        cart,
        plan
      );


    /*
     * User may have removed Service
     * using quantity control.
     */

    if (
      desiredQty <= 0
    ) {

      await handleServiceRemoval(
        plan,
        cart
      );


      return;
    }


    /*
     * Exactly 1 Device and 1 Service:
     * Device follows Service.
     */

    if (
      devices.length === 1 &&
      services.length === 1
    ) {

      const device =
        devices[0];


      if (
        Number(device.quantity) !==
        Number(desiredQty)
      ) {

        log(
          `[WELLPOINT] ${plan}: Service -> Device ${device.quantity} -> ${desiredQty}`
        );


        await updateCart({
          [device.key]:
            desiredQty
        });
      }


      return;
    }


    /*
     * Multiple devices:
     * cannot know which Device to change.
     */

    showServiceWarning(
      plan
    );


    await syncPlan(
      plan,
      cart
    );
  }


  /*
   * ========================================================
   * QUEUE WORKER
   * ========================================================
   */

  function startWorker(plan) {

    if (
      runningWorkers.has(plan)
    ) {

      return;
    }


    const worker =
      (async () => {

        try {

          while (
            pendingChanges.has(plan)
          ) {

            const change =
              pendingChanges.get(plan);


            pendingChanges.delete(
              plan
            );


            /*
             * DEVICE
             */

            if (
              change.type ===
              'DEVICE'
            ) {

              await waitForBackend(
                change.item,
                change.quantity
              );


              if (
                pendingChanges.has(plan)
              ) {

                continue;
              }


              await syncPlan(
                plan
              );
            }


            /*
             * SERVICE QTY
             */

            else if (
              change.type ===
              'SERVICE'
            ) {

              await serviceToSingleDevice(
                plan,
                change.item,
                change.quantity
              );
            }


            /*
             * SERVICE REMOVE
             */

            else if (
              change.type ===
              'SERVICE_REMOVE'
            ) {

              const cart =
                await waitForBackend(
                  change.item,
                  0
                );


              await handleServiceRemoval(
                plan,
                cart
              );
            }


            await refreshCartSnapshot();


            markRefreshNeeded();
          }

        } catch (e) {

          error(
            `[WELLPOINT] ${plan} worker error:`,
            e
          );

        } finally {

          runningWorkers.delete(
            plan
          );


          if (
            pendingChanges.has(plan)
          ) {

            startWorker(
              plan
            );

          } else {

            scheduleRefresh();
          }
        }

      })();


    runningWorkers.set(
      plan,
      worker
    );
  }


  function queueChange(
    plan,
    change
  ) {

    cancelRefresh();


    pendingChanges.set(
      plan,
      change
    );


    startWorker(
      plan
    );
  }


  /*
   * ========================================================
   * QUANTITY CHANGE
   * ========================================================
   */

  document.addEventListener(
    'change',
    event => {

      const input =
        event.target;


      if (
        !input?.matches?.(
          QUANTITY_SELECTOR
        )
      ) {

        return;
      }


      if (!cartSnapshot) {
        return;
      }


      cancelRefresh();


      const lineIndex =
        getLineIndex(
          input
        );


      if (lineIndex <= 0) {
        return;
      }


      const item =
        cartSnapshot.items?.[
          lineIndex - 1
        ];


      if (!item) {
        return;
      }


      const identified =
        identifyItem(item);


      if (!identified) {
        return;
      }


      const desiredQty =
        Math.max(
          0,
          parseInt(
            input.value,
            10
          ) || 0
        );


      if (
        identified.type ===
        'device'
      ) {

        log(
          `[WELLPOINT] ${identified.plan}: Device qty -> ${desiredQty}`
        );


        queueChange(
          identified.plan,
          {
            type:
              'DEVICE',

            item:
              item,

            quantity:
              desiredQty
          }
        );


        return;
      }


      /*
       * SERVICE
       */

      if (
        desiredQty <= 0
      ) {

        queueChange(
          identified.plan,
          {
            type:
              'SERVICE_REMOVE',

            item:
              item,

            quantity:
              0
          }
        );


        return;
      }


      queueChange(
        identified.plan,
        {
          type:
            'SERVICE',

          item:
            item,

          quantity:
            desiredQty
        }
      );

    },

    true
  );


  /*
   * ========================================================
   * REMOVE
   * ========================================================
   */

  document.addEventListener(
    'click',
    event => {

      const remove =
        event.target
          ?.closest
          ?.(
            REMOVE_SELECTOR
          );


      if (
        !remove ||
        !cartSnapshot
      ) {

        return;
      }


      cancelRefresh();


      const lineIndex =
        getLineIndex(
          remove
        );


      if (lineIndex <= 0) {
        return;
      }


      const item =
        cartSnapshot.items?.[
          lineIndex - 1
        ];


      if (!item) {
        return;
      }


      const identified =
        identifyItem(item);


      if (!identified) {
        return;
      }


      if (
        identified.type ===
        'service'
      ) {

        log(
          `[WELLPOINT] ${identified.plan}: Service remove`
        );


        queueChange(
          identified.plan,
          {
            type:
              'SERVICE_REMOVE',

            item:
              item,

            quantity:
              0
          }
        );


        return;
      }


      /*
       * Device removal.
       */

      queueChange(
        identified.plan,
        {
          type:
            'DEVICE',

          item:
            item,

          quantity:
            0
        }
      );

    },

    true
  );


  /*
   * ========================================================
   * ADD TO CART MONITOR
   * ========================================================
   */

  document.addEventListener(
    'submit',
    event => {

      const form =
        event.target;


      if (
        !form?.matches?.(
          'form[action*="/cart/add"]'
        )
      ) {

        return;
      }


      const beforeCart =
        cartSnapshot;


      if (!beforeCart) {
        return;
      }


      cancelRefresh();


      monitorAdd(
        beforeCart
      ).catch(
        e => {

          error(
            '[WELLPOINT] Add monitor error:',
            e
          );
        }
      );

    },

    true
  );


  async function monitorAdd(
    beforeCart
  ) {

    const before = {};


    VALID_PLANS.forEach(
      plan => {

        before[plan] =
          getDeviceTotal(
            beforeCart,
            plan
          );
      }
    );


    const started =
      Date.now();


    while (
      Date.now() -
      started <
      ADD_MONITOR_MS
    ) {

      await sleep(
        BACKEND_POLL_MS
      );


      const afterCart =
        await getCart();


      let changed =
        false;


      for (
        const plan of
        VALID_PLANS
      ) {

        const after =
          getDeviceTotal(
            afterCart,
            plan
          );


        if (
          after !==
          before[plan]
        ) {

          log(
            `[WELLPOINT] ${plan}: Device total ${before[plan]} -> ${after}`
          );


          changed =
            true;
        }
      }


      if (changed) {

        await syncAllPlans();


        await refreshCartSnapshot();


        markRefreshNeeded();


        scheduleRefresh();


        return;
      }
    }


    warn(
      '[WELLPOINT] Add monitor timeout'
    );
  }


  /*
   * ========================================================
   * INITIALIZE
   * ========================================================
   */

  async function initialize() {

    try {

      log(
        `[WELLPOINT Bundle] Initializing v${SCRIPT_VERSION}`
      );


      log(
        `[WELLPOINT] Device title filter = "${SETTINGS.deviceProductTitleFilter || ''}"`
      );


      log(
        `[WELLPOINT] SERVICE_REMOVE_BEHAVIOR = ${SERVICE_REMOVE_BEHAVIOR}`
      );


      await resolveAllServices();


      cartSnapshot =
        await getCart();


      const changed =
        await syncAllPlans();


      await refreshCartSnapshot();


      if (changed) {

        markRefreshNeeded();


        scheduleRefresh();
      }


      log(
        `[WELLPOINT Bundle] v${SCRIPT_VERSION} ready`
      );

    } catch (e) {

      error(
        '[WELLPOINT] Initialization error:',
        e
      );
    }
  }


  if (
    document.readyState ===
    'loading'
  ) {

    document.addEventListener(
      'DOMContentLoaded',
      initialize
    );

  } else {

    initialize();
  }

})();

/* EOF SAJAT - WELLPOINT Service Bundle */